import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCoinTransferHistory, useUserCoinBalance, useTransferCoins } from '@/hooks/useUserCoinBalance';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Coins, History, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface CoinTransferHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
}

export function CoinTransferHistoryDialog({ open, onOpenChange, userName }: CoinTransferHistoryDialogProps) {
  const { data: transfers = [], isLoading } = useCoinTransferHistory(userName);
  const { data: balance } = useUserCoinBalance(userName);
  const transferCoins = useTransferCoins();

  const handleTransfer = () => {
    if (!balance || balance.balance === 0) {
      toast.error('אין מטבעות להעברה');
      return;
    }
    
    transferCoins.mutate(userName, {
      onSuccess: () => {
        toast.success(`הועברו ${balance.balance} מטבעות בהצלחה!`);
      },
      onError: () => {
        toast.error('שגיאה בהעברת המטבעות');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-slate-100" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-slate-100">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-500" />
              היסטוריית העברות מטבעות
            </div>
            {balance && balance.balance > 0 && (
              <Button
                size="sm"
                onClick={handleTransfer}
                disabled={transferCoins.isPending}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              >
                <Send className="w-4 h-4 ml-1" />
                העבר אליי ({balance.balance})
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {isLoading ? (
            <div className="text-center py-8 text-slate-400">טוען...</div>
          ) : transfers.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Coins className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>אין היסטוריית העברות</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {transfers.map((transfer) => (
                <div 
                  key={transfer.id}
                  className="flex items-center justify-between p-3 bg-amber-900/30 rounded-lg border border-amber-700"
                >
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-amber-500" />
                    <span className="font-bold text-amber-400">{transfer.amount}</span>
                    <span className="text-sm text-slate-400">מטבעות</span>
                  </div>
                  <div className="text-xs text-slate-400">
                    {format(new Date(transfer.transferred_at), 'dd/MM/yyyy HH:mm', { locale: he })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
